import { useState, useMemo } from 'react'
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
  LineChart,
  Line,
  RadarChart,
  Radar,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
} from 'recharts'
import { useArticles, useRegulatoryItems } from '@/hooks/queries'
import { Skeleton } from '@/components/ui/skeleton'
import { KpiMetricCard } from '@/components/shared/KpiMetricCard'
import type { Article, RegulatoryItem, Section, Category, RegulatoryType } from '@/lib/types'
import * as ontology from '@intelhub/shared/ontology'

type TimeRange = '7d' | '30d' | '90d'

// Categorical palette (Paul Tol, 8 colours) sourced from design-playbook tokens.
// Auto-adapts to light/dark via `[data-mode]` selectors in tokens-dark.css.
const CHART_COLOURS = [
  'var(--cat-1)',
  'var(--cat-2)',
  'var(--cat-3)',
  'var(--cat-4)',
  'var(--cat-5)',
  'var(--cat-6)',
  'var(--cat-7)',
  'var(--cat-8)',
]

// Shared tooltip styling — mirrors SP4 analytics chart components so dark mode
// flips automatically via tokens. Tokens are hex, used as var(--token) (no hsl()
// wrapper; the earlier hsl(var(--X)) form produced invalid hsl(#hex) strings).
const tooltipContentStyle = {
  background: 'var(--surface-elevated)',
  border: '1px solid var(--border-default)',
  borderRadius: 6,
  color: 'var(--text-primary)',
  fontSize: 12,
} as const

// Axis/grid/legend token-bound style fragments. Recharts ignores Tailwind
// `dark:` utilities on SVG text, so we wire tick fills directly.
const axisTickStyle = {
  fontSize: 11,
  fill: 'var(--chart-axis-label)',
} as const

const axisSmallTickStyle = {
  fontSize: 9,
  fill: 'var(--chart-axis-label)',
} as const

const legendWrapperStyle = {
  fontSize: 11,
  color: 'var(--text-primary)',
} as const

// Generate label maps from ontology — keys use underscore_case to match DB values
const SECTION_LABELS: Record<Section, string> = Object.fromEntries(
  ontology.contentSections.map((e) => [e.slug.replace(/-/g, '_'), e.name])
) as Record<Section, string>

const CATEGORY_LABELS: Record<Category, string> = Object.fromEntries(
  ontology.contentCategories
    .filter((e) => ['threat', 'vulnerability', 'cloud', 'identity', 'regulation', 'risk', 'supply-chain'].includes(e.slug))
    .map((e) => [e.slug.replace(/-/g, '_'), e.name])
) as Record<Category, string>

const REGULATORY_TYPE_LABELS: Record<RegulatoryType, string> = {
  new_rule: 'New Rule',
  guidance: 'Guidance',
  enforcement: 'Enforcement',
  deadline: 'Deadline',
}

function filterByTimeRange(articles: Article[], range: TimeRange): Article[] {
  const ms: Record<TimeRange, number> = {
    '7d': 7 * 24 * 60 * 60 * 1000,
    '30d': 30 * 24 * 60 * 60 * 1000,
    '90d': 90 * 24 * 60 * 60 * 1000,
  }
  const cutoff = new Date(Date.now() - ms[range])
  return articles.filter((a) => new Date(a.publishedAt) >= cutoff)
}


export function AnalyticsPage() {
  const [range, setRange] = useState<TimeRange>('30d')

  const { data: allArticles, isLoading: articlesLoading } = useArticles()
  const { data: regulatoryItems, isLoading: regLoading } = useRegulatoryItems()

  const articles = useMemo(
    () => filterByTimeRange(allArticles ?? [], range),
    [allArticles, range],
  )

  const isLoading = articlesLoading || regLoading

  // Summary stats
  const totalArticles = articles.length
  const criticalCount = articles.filter((a) => a.severity === 'critical').length
  const avgRiskScore =
    articles.length > 0
      ? Math.round(articles.reduce((sum, a) => sum + a.riskScore, 0) / articles.length)
      : 0
  const regCount = (regulatoryItems ?? []).length

  // Category distribution
  const categoryData = useMemo(() => {
    const counts: Record<string, number> = {}
    articles.forEach((a) => {
      const key = a.category ? CATEGORY_LABELS[a.category] : 'Unknown'
      counts[key] = (counts[key] ?? 0) + 1
    })
    return Object.entries(counts)
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => b.count - a.count)
  }, [articles])

  // Risk score distribution
  const riskDistData = useMemo(() => {
    const bands = [
      { name: '0–20', min: 0, max: 20, count: 0 },
      { name: '21–40', min: 21, max: 40, count: 0 },
      { name: '41–60', min: 41, max: 60, count: 0 },
      { name: '61–80', min: 61, max: 80, count: 0 },
      { name: '81–100', min: 81, max: 100, count: 0 },
    ]
    articles.forEach((a) => {
      const band = bands.find((b) => a.riskScore >= b.min && a.riskScore <= b.max)
      if (band) band.count++
    })
    return bands
  }, [articles])

  // Jurisdiction donut
  const jurisdictionData = useMemo(() => {
    const counts: Record<string, number> = {}
    articles.forEach((a) => {
      const regions = a.jurisdiction ?? ['Unknown']
      regions.forEach((r) => {
        counts[r] = (counts[r] ?? 0) + 1
      })
    })
    return Object.entries(counts)
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 8)
  }, [articles])

  // Confidence donut
  const confidenceData = useMemo(() => {
    const counts: Record<string, number> = {}
    articles.forEach((a) => {
      const key = a.confidence ?? 'unknown'
      counts[key] = (counts[key] ?? 0) + 1
    })
    return Object.entries(counts).map(([name, value]) => ({ name, value }))
  }, [articles])

  // Section radar
  const radarData = useMemo(() => {
    const counts: Record<string, number> = {}
    articles.forEach((a) => {
      if (a.section) {
        const key = SECTION_LABELS[a.section]
        counts[key] = (counts[key] ?? 0) + 1
      }
    })
    return Object.entries(counts).map(([subject, count]) => ({ subject, count }))
  }, [articles])

  // Regulatory by type
  const regByTypeData = useMemo(() => {
    const counts: Record<string, number> = {}
    ;(regulatoryItems ?? []).forEach((r: RegulatoryItem) => {
      const key = REGULATORY_TYPE_LABELS[r.type]
      counts[key] = (counts[key] ?? 0) + 1
    })
    return Object.entries(counts).map(([name, count]) => ({ name, count }))
  }, [regulatoryItems])

  // Articles over time (line chart)
  const articlesOverTime = useMemo(() => {
    const granularity = range === '7d' ? 'day' : range === '30d' ? 'day' : 'week'

    const buckets: Record<string, number> = {}
    articles.forEach((a) => {
      const d = new Date(a.publishedAt)
      let key: string
      if (granularity === 'day') {
        key = d.toISOString().slice(0, 10)
      } else {
        // ISO week
        const week = Math.floor(d.getTime() / (7 * 24 * 60 * 60 * 1000))
        key = `W${week}`
      }
      buckets[key] = (buckets[key] ?? 0) + 1
    })

    return Object.entries(buckets)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([date, count]) => ({ date, count }))
  }, [articles, range])

  if (isLoading) {
    return (
      <div className="p-4 md:p-6 max-w-7xl mx-auto space-y-4">
        <Skeleton className="h-8 w-48" />
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-20" />
          ))}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-64" />
          ))}
        </div>
      </div>
    )
  }

  return (
    <main className="flex flex-col gap-6 p-4 md:p-6 max-w-7xl mx-auto">
      <header className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold">Analytics</h1>
          <p className="text-muted-foreground text-sm mt-0.5">
            Intelligence coverage metrics and trends.
          </p>
        </div>
        <div data-testid="filter-bar" className="flex gap-1 rounded-lg border p-1 bg-muted">
          {(['7d', '30d', '90d'] as TimeRange[]).map((r) => (
            <button
              key={r}
              type="button"
              onClick={() => setRange(r)}
              className={`rounded-md px-4 py-2 min-h-[44px] text-xs font-medium transition-colors sm:px-3 sm:py-1 sm:min-h-0 ${
                range === r
                  ? 'bg-background shadow-sm text-foreground'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              {r}
            </button>
          ))}
        </div>
      </header>

      {/* Summary cards */}
      <div data-testid="kpi-rail" className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <KpiMetricCard label="Articles" value={totalArticles} />
        <KpiMetricCard label="Critical" value={criticalCount} />
        <KpiMetricCard label="Avg Risk Score" value={avgRiskScore} />
        <KpiMetricCard label="Regulatory Items" value={regCount} />
      </div>

      <section
        data-testid="chart-shell"
        data-chart-shell="true"
        className="min-h-[480px] rounded-md border p-4 flex flex-col gap-4"
      >
        {/* Charts grid — row 1 */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {/* Category distribution bar */}
        <div className="rounded-lg border bg-card p-4">
          <h3 className="text-sm font-semibold mb-3">Category Distribution</h3>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={categoryData} margin={{ top: 0, right: 0, left: -20, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--chart-grid-stroke)" />
              <XAxis dataKey="name" tick={axisTickStyle} stroke="var(--chart-axis-stroke)" />
              <YAxis tick={axisTickStyle} stroke="var(--chart-axis-stroke)" />
              <Tooltip
                cursor={{ fill: 'var(--surface-tertiary)', opacity: 0.3 }}
                contentStyle={tooltipContentStyle}
              />
              <Bar dataKey="count" fill="var(--chart-primary)" fillOpacity={0.85} radius={[2, 2, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Risk score distribution */}
        <div className="rounded-lg border bg-card p-4">
          <h3 className="text-sm font-semibold mb-3">Risk Score Distribution</h3>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={riskDistData} margin={{ top: 0, right: 0, left: -20, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--chart-grid-stroke)" />
              <XAxis dataKey="name" tick={axisTickStyle} stroke="var(--chart-axis-stroke)" />
              <YAxis tick={axisTickStyle} stroke="var(--chart-axis-stroke)" />
              <Tooltip
                cursor={{ fill: 'var(--surface-tertiary)', opacity: 0.3 }}
                contentStyle={tooltipContentStyle}
              />
              <Bar dataKey="count" fill="var(--severity-high)" fillOpacity={0.85} radius={[2, 2, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Charts grid — row 2 */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {/* Jurisdiction donut */}
        <div className="rounded-lg border bg-card p-4">
          <h3 className="text-sm font-semibold mb-3">Jurisdiction Distribution</h3>
          <ResponsiveContainer width="100%" height={220}>
            <PieChart>
              <Pie
                data={jurisdictionData}
                cx="50%"
                cy="50%"
                innerRadius={50}
                outerRadius={80}
                dataKey="value"
                nameKey="name"
              >
                {jurisdictionData.map((_, i) => (
                  <Cell key={i} fill={CHART_COLOURS[i % CHART_COLOURS.length]} />
                ))}
              </Pie>
              <Tooltip contentStyle={tooltipContentStyle} />
              <Legend iconSize={10} wrapperStyle={legendWrapperStyle} />
            </PieChart>
          </ResponsiveContainer>
        </div>

        {/* Confidence donut */}
        <div className="rounded-lg border bg-card p-4">
          <h3 className="text-sm font-semibold mb-3">Confidence Level</h3>
          <ResponsiveContainer width="100%" height={220}>
            <PieChart>
              <Pie
                data={confidenceData}
                cx="50%"
                cy="50%"
                innerRadius={50}
                outerRadius={80}
                dataKey="value"
                nameKey="name"
              >
                {confidenceData.map((_, i) => (
                  <Cell key={i} fill={CHART_COLOURS[i % CHART_COLOURS.length]} />
                ))}
              </Pie>
              <Tooltip contentStyle={tooltipContentStyle} />
              <Legend iconSize={10} wrapperStyle={legendWrapperStyle} />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Charts grid — row 3 */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {/* Section radar */}
        <div className="rounded-lg border bg-card p-4">
          <h3 className="text-sm font-semibold mb-3">Coverage by Section</h3>
          {radarData.length > 0 ? (
            <ResponsiveContainer width="100%" height={240}>
              <RadarChart data={radarData}>
                <PolarGrid stroke="var(--chart-grid-stroke)" />
                <PolarAngleAxis dataKey="subject" tick={axisTickStyle} stroke="var(--chart-axis-stroke)" />
                <PolarRadiusAxis tick={axisSmallTickStyle} stroke="var(--chart-axis-stroke)" />
                <Radar
                  name="Articles"
                  dataKey="count"
                  stroke="var(--chart-primary-strong)"
                  fill="var(--chart-primary)"
                  fillOpacity={0.3}
                />
                <Tooltip contentStyle={tooltipContentStyle} />
              </RadarChart>
            </ResponsiveContainer>
          ) : (
            <p className="text-sm text-muted-foreground">No section data.</p>
          )}
        </div>

        {/* Regulatory by type */}
        <div className="rounded-lg border bg-card p-4">
          <h3 className="text-sm font-semibold mb-3">Regulatory Items by Type</h3>
          {regByTypeData.length > 0 ? (
            <ResponsiveContainer width="100%" height={240}>
              <BarChart
                data={regByTypeData}
                layout="vertical"
                margin={{ top: 0, right: 16, left: 20, bottom: 0 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="var(--chart-grid-stroke)" />
                <XAxis type="number" tick={axisTickStyle} stroke="var(--chart-axis-stroke)" />
                <YAxis
                  dataKey="name"
                  type="category"
                  tick={axisTickStyle}
                  stroke="var(--chart-axis-stroke)"
                  width={80}
                />
                <Tooltip
                  cursor={{ fill: 'var(--surface-tertiary)', opacity: 0.3 }}
                  contentStyle={tooltipContentStyle}
                />
                <Bar dataKey="count" fill="var(--chart-success)" fillOpacity={0.85} radius={[0, 2, 2, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <p className="text-sm text-muted-foreground">No regulatory data.</p>
          )}
        </div>
      </div>

      {/* Articles over time line chart */}
      <div className="rounded-lg border bg-card p-4">
        <h3 className="text-sm font-semibold mb-3">Articles Over Time</h3>
        {articlesOverTime.length > 0 ? (
          <ResponsiveContainer width="100%" height={200}>
            <LineChart
              data={articlesOverTime}
              margin={{ top: 0, right: 16, left: -20, bottom: 0 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="var(--chart-grid-stroke)" />
              <XAxis
                dataKey="date"
                tick={{ fontSize: 10, fill: 'var(--chart-axis-label)' }}
                stroke="var(--chart-axis-stroke)"
                tickFormatter={(v: string) => v.slice(5)}
              />
              <YAxis tick={axisTickStyle} stroke="var(--chart-axis-stroke)" />
              <Tooltip contentStyle={tooltipContentStyle} />
              <Line
                type="monotone"
                dataKey="count"
                stroke="var(--chart-primary-strong)"
                strokeWidth={2}
                dot={false}
              />
            </LineChart>
          </ResponsiveContainer>
        ) : (
          <p className="text-sm text-muted-foreground">No articles in the selected period.</p>
        )}
        </div>
      </section>
    </main>
  )
}
